import type { ChartData } from 'chart.js'

import { DoughnutChart } from '@/components/charts/doughnut-chart.tsx'
import { EmptyChartData } from '@/components/charts/empty-chart-data.tsx'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import type { TopChatterGraphType } from '@/modules/tracking/chatter-tracking/chatter-tracking-page.tsx'
import type { ChatterLegendOptions } from '@/modules/tracking/chatter-tracking/components/top-chatters-legend.tsx'
import { TopChattersLegend } from '@/modules/tracking/chatter-tracking/components/top-chatters-legend.tsx'

const legendTitles: Record<TopChatterGraphType, string> = {
  Revenue: 'Total Revenue',
  Activity: 'Total Activity',
  MessageAmount: 'Amount of Messages',
  ResponseTime: 'Response Time',
}

interface Props {
  data: ChartData<'doughnut', number[], unknown> | undefined
  legendData?: ChatterLegendOptions['legendData']
  isFetching: boolean
  topChatterGraphType: TopChatterGraphType
  onChangeGraphType: (value: TopChatterGraphType) => void
  isTooltipCurrency: boolean
  isTooltipMinutes: boolean
  isTooltipSeconds: boolean
  showCents?: boolean
  hideTotal?: boolean
}

export const TopChatterGraph = ({
  data,
  legendData,
  isFetching,
  topChatterGraphType,
  onChangeGraphType,
  isTooltipCurrency,
  isTooltipMinutes,
  isTooltipSeconds,
  showCents,
  hideTotal,
}: Props) => {
  return (
    <Card className="flex flex-col justify-between">
      <CardHeader>
        <CardTitle>
          <div className="flex items-center justify-between">
            <span className="text-xl font-semibold leading-none">
              Top Chatters
            </span>
            <Select
              value={topChatterGraphType}
              onValueChange={(value) =>
                onChangeGraphType(value as TopChatterGraphType)
              }
            >
              <SelectTrigger className="w-auto font-normal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="Revenue">Revenue</SelectItem>
                  <SelectItem value="Activity">Activity</SelectItem>
                  <SelectItem value="MessageAmount">
                    Amount of Messages
                  </SelectItem>
                  <SelectItem value="ResponseTime">Response Time</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex content-center items-center justify-center pb-8 sm:mb-12 sm:pb-0">
        {isFetching || !data || !legendData ? (
          <EmptyChartData isFetching={isFetching} />
        ) : legendData.total.length === 0 ? (
          <div className="pb-6">
            <EmptyChartData
              isFetching={isFetching}
              text="Chatters didn't earn any revenue in this period."
            />
          </div>
        ) : (
          <div className="gap- flex w-full flex-col items-center justify-center sm:flex-row sm:pr-4 2xl:justify-between">
            <div className=" h-56">
              <DoughnutChart
                data={data}
                toggleOptions={{
                  showLegendTitle: true,
                  isTooltipCurrency: isTooltipCurrency,
                  isTooltipMinutes: isTooltipMinutes,
                  isTooltipSeconds: isTooltipSeconds,
                  hideLegend: true,
                }}
                showCents={showCents}
              />
            </div>
            <TopChattersLegend
              title={legendTitles[topChatterGraphType]}
              legendData={legendData}
              hideTotal={hideTotal}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
